import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDown, CircleHelp, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import { useCurrentUser } from '@/features/auth/useAuth';
import { ConflictChoiceSheet } from '@/features/calendars/ConflictChoiceSheet';
import { captureIntent, describeIntent } from '@/features/calendars/quickCapture';
import { useCapturePlacement } from '@/features/calendars/useCapturePlacement';
import { cn } from '@/lib/cn';
import { motionSafe, springDefault } from '@/lib/motion';
import { toast } from '@/lib/toast';
import { parseQuickAdd } from './quickAddParser';
import { QUICK_ADD_KEYS, QUICK_ADD_SHORTCUTS } from './quickAddShortcuts';
import { formatDuration } from './tree';
import { useClients, useCreateTask, useProjects } from './useTasks';

const DRAFT_KEY = 'chronos-quickadd-draft';

const CHIP_TONE: Record<string, string> = {
  estimate: 'bg-surface-3 text-text-secondary',
  priority: 'bg-danger-quiet text-danger',
  client: 'bg-accent-quiet text-accent',
  project: 'bg-accent-quiet text-accent',
  energy: 'bg-surface-3 text-text-secondary',
  due: 'bg-warning-quiet text-warning',
  schedule: 'bg-accent-quiet text-accent',
};

export function QuickAdd({ defaultClientId }: { defaultClientId?: number }) {
  // Draft survives reload. An interruption mid-thought must never cost the
  // thought -- that is the whole reason capture is fast in the first place.
  const [input, setInput] = useState(() => localStorage.getItem(DRAFT_KEY) ?? '');
  const [expanded, setExpanded] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notes, setNotes] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const helpId = useId();

  const clients = useClients();
  const projects = useProjects();
  const createTask = useCreateTask();
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const placement = useCapturePlacement({
    onPlaced: (title) => toast.show(`Scheduled "${title}"`),
  });

  const parsed = useMemo(
    () =>
      parseQuickAdd(input, {
        clients: clients.data ?? [],
        projects: projects.data ?? [],
      }),
    [input, clients.data, projects.data],
  );

  useEffect(() => {
    if (input) localStorage.setItem(DRAFT_KEY, input);
    else localStorage.removeItem(DRAFT_KEY);
  }, [input]);

  // `C` focuses capture from anywhere. Two taps to capture, from any screen.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'c' || event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (typing) return;

      event.preventDefault();
      inputRef.current?.focus();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // `>fri 9am` is a plan, not a deadline: it lands on the calendar, and any
  // separate due date in the same line still gets stored on the task.
  const intent = useMemo(
    () =>
      captureIntent(parsed, {
        timezone,
        dayStartMinute: user?.day_start_minute ?? undefined,
      }),
    [parsed, timezone, user?.day_start_minute],
  );
  const when = describeIntent(intent, timezone);

  const clientId = parsed.client_id ?? defaultClientId ?? clients.data?.[0]?.id;
  const canSave = parsed.title.length > 0 && clientId !== undefined;

  const reset = () => {
    setInput('');
    setNotes('');
    setExpanded(false);
    localStorage.removeItem(DRAFT_KEY);
  };

  const save = () => {
    if (!canSave) return;

    createTask.mutate(
      {
        title: parsed.title,
        notes: notes || null,
        client_id: clientId,
        project_id: parsed.project_id,
        estimate_minutes: parsed.estimate_minutes,
        ...(parsed.priority ? { priority: parsed.priority } : {}),
        ...(parsed.energy ? { energy: parsed.energy } : {}),
        due_at: intent.dueAt?.toISOString() ?? null,
      },
      {
        onSuccess: (task) => {
          if (intent.kind === 'schedule' && intent.startsAt && intent.endsAt) {
            void placement.place(task.id, task.title, intent.startsAt, intent.endsAt);
          } else {
            toast.undoable(`Added "${task.title}"`, () => createTask.undo(task.id));
          }
          reset();
          inputRef.current?.focus();
        },
        onError: (error) => toast.error(error.message),
      },
    );
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      save();
      return;
    }
    if (event.key === 'Escape') {
      // Close help first; then blur but keep the draft. Escape means "get out
      // of my way", not "throw away what I just wrote".
      if (helpOpen) {
        event.preventDefault();
        setHelpOpen(false);
        return;
      }
      event.currentTarget.blur();
      setExpanded(false);
      return;
    }
    if (event.key === 'Tab' && !event.shiftKey && input && !expanded) {
      event.preventDefault();
      setExpanded(true);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-surface-1 shadow-chip">
      <div className="flex items-center gap-2 px-3">
        <Plus size={16} strokeWidth={1.5} className="shrink-0 text-text-tertiary" aria-hidden />
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          // Screen readers get the syntax; sighted users get the chips + help.
          aria-label="Add a task. Supports ~45m for an estimate, exclamation 1 to 4 for priority, at-sign for client, hash for project, slash for energy, plain dates for a due date, and greater-than for when to schedule it, such as greater-than friday 9am."
          aria-describedby={helpOpen ? helpId : undefined}
          placeholder="Add a task…"
          className="min-h-touch flex-1 bg-transparent text-body outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-text-tertiary"
        />

        <button
          type="button"
          onClick={() => setHelpOpen((v) => !v)}
          aria-expanded={helpOpen}
          aria-controls={helpId}
          aria-label={helpOpen ? 'Hide shortcuts' : 'Show shortcuts'}
          title={helpOpen ? 'Hide shortcuts' : 'Show shortcuts'}
          className={cn(
            'pressable shrink-0 rounded p-1 text-text-tertiary hover:text-text',
            helpOpen && 'text-accent',
          )}
        >
          <CircleHelp size={16} strokeWidth={1.5} aria-hidden />
        </button>

        {input && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="shrink-0 rounded p-1 text-text-tertiary hover:text-text"
            title={expanded ? 'Fewer fields' : 'More fields'}
          >
            <motion.span
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={motionSafe(springDefault)}
              className="flex"
            >
              <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
            </motion.span>
          </button>
        )}
      </div>

      {/* Chips make the parse visible. The user always sees exactly what was
          understood, so a wrong guess is obvious rather than silent. */}
      <AnimatePresence initial={false}>
        {parsed.tokens.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={motionSafe(springDefault)}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap gap-1.5 px-3 pb-2 pl-9">
              {parsed.tokens.map((token) => (
                <button
                  key={`${token.type}-${token.raw}`}
                  type="button"
                  // Removing a chip removes its source text, so the field and
                  // the chips can never disagree about what is set.
                  onClick={() => setInput((v) => v.replace(token.raw, '').replace(/\s+/g, ' ').trim())}
                  title={`Remove ${token.raw}`}
                  className={cn(
                    'pressable rounded-full px-2 py-0.5 text-label font-medium uppercase',
                    CHIP_TONE[token.type],
                  )}
                >
                  {token.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {helpOpen && (
          <motion.div
            id={helpId}
            role="region"
            aria-label="Quick Add shortcuts"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={motionSafe(springDefault)}
            className="overflow-hidden"
          >
            <div className="border-t border-border px-3 py-3 pl-9">
              <p className="mb-2 text-small text-text-secondary">
                Type tokens anywhere in the title. Chips appear as they match, click a chip to
                remove it.
              </p>
              <ul className="flex flex-col gap-2">
                {QUICK_ADD_SHORTCUTS.map((row) => (
                  <li key={row.tone} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="flex flex-wrap gap-1">
                      {row.examples.map((example) => (
                        <code
                          key={example}
                          className={cn(
                            'rounded-full px-2 py-0.5 font-mono text-label font-medium',
                            CHIP_TONE[row.tone],
                          )}
                        >
                          {example}
                        </code>
                      ))}
                    </span>
                    <span className="text-small text-text-tertiary">{row.meaning}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-label uppercase text-text-tertiary">Keys</p>
              <ul className="mt-1.5 flex flex-col gap-1">
                {QUICK_ADD_KEYS.map((row) => (
                  <li key={row.key} className="flex items-baseline gap-2 text-small">
                    <kbd className="rounded border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-label text-text-secondary">
                      {row.key}
                    </kbd>
                    <span className="text-text-tertiary">{row.action}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-small text-text-tertiary">
                Example:{' '}
                <code className="font-mono text-text-secondary">
                  Fix login bug ~45m !1 @acme #redesign friday &gt;thu 2pm
                </code>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expands in place, pushing content down. Not a modal: a dialog would
          replace the thing being edited with a different surface. */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={motionSafe(springDefault)}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-3 border-t border-border p-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-small font-medium text-text-secondary">Notes</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="rounded border border-border bg-surface-1 p-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
                />
              </label>

              <dl className="grid grid-cols-2 gap-2 text-small">
                <Summary label="Estimate" value={formatDuration(parsed.estimate_minutes)} />
                <Summary label="Priority" value={parsed.priority} />
                <Summary
                  label="Client"
                  value={clients.data?.find((c) => c.id === clientId)?.name ?? null}
                />
                <Summary
                  label="Project"
                  value={projects.data?.find((p) => p.id === parsed.project_id)?.name ?? null}
                />
              </dl>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {input && (
        <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2">
          {/* Where it lands, said before Enter, the difference between a
              deadline and a plan is learned by watching this line change. */}
          <p className="truncate text-small text-text-tertiary">
            {when ? (
              <span className="tabular text-accent">{when}</span>
            ) : (
              parsed.title || 'Add a title'
            )}
          </p>
          <Button
            variant="primary"
            onClick={save}
            disabled={!canSave}
            loading={createTask.isPending}
            className="px-4 text-small"
          >
            Add
          </Button>
        </div>
      )}

      {placement.pending && (
        <ConflictChoiceSheet
          taskTitle={placement.pending.taskTitle}
          startsAt={placement.pending.startsAt}
          endsAt={placement.pending.endsAt}
          timezone={timezone}
          canSplit={placement.canSplit}
          // Cancelling keeps the task: only its placement is in question.
          onClose={placement.cancel}
          onChoose={(choice) =>
            choice.mode === 'split' ? placement.split() : placement.overlap()
          }
        />
      )}
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-label uppercase text-text-tertiary">{label}</dt>
      <dd className={cn('mt-0.5', value ? 'text-text' : 'text-text-tertiary')}>
        {value ?? '-'}
      </dd>
    </div>
  );
}
